import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, RouterLink } from '@angular/router';

type DashboardStat = {
  label: string;
  suffix?: string;
  icon: string;
  tone: 'blue' | 'green' | 'red' | 'amber' | 'violet';
};

type DashboardStatWithValue = DashboardStat & {
  value: string;
};

type PssRow = {
  code: string;
  name: string;
  qcaCode: string;
  qcaName: string;
  state: string;
  district: string;
  voltageLevelKv: number;
  installedCapacityMw: number;
  feederCount: number;
  generatorCount: number;
  meterCount: number;
  status: 'Active' | 'Inactive';
  commissionDate: string;
};

type LinkedGeneratingCompany = {
  registrationNo: string;
  entityName: string;
  username: string;
  email: string;
};

type PssManagementMock = {
  header: {
    title: string;
    description: string;
  };
  stats: DashboardStat[];
  filters: {
    state: string[];
    district: string[];
    voltageLevelKv: string[];
    status: string[];
    qca: string[];
  };
  rows: PssRow[];
  generatingCompanyCatalog: LinkedGeneratingCompany[];
  pssGeneratingCompanyMappings: Record<string, string[]>;
};

const DEFAULT_DATA: PssManagementMock = {
  header: {
    title: 'Manage PSS',
    description: 'Review PSS registrations and manage linked generating companies.'
  },
  stats: [],
  filters: {
    state: ['All'],
    district: ['All'],
    voltageLevelKv: ['All'],
    status: ['All'],
    qca: ['All']
  },
  rows: [],
  generatingCompanyCatalog: [],
  pssGeneratingCompanyMappings: {}
};

@Component({
  selector: 'app-pss-registration',
  standalone: true,
  imports: [RouterLink],
  templateUrl: './pss-registration.component.html',
  styleUrls: ['./pss-registration.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PssRegistrationComponent {
  private readonly http = inject(HttpClient);
  private readonly route = inject(ActivatedRoute);

  readonly pageData = signal<PssManagementMock>(DEFAULT_DATA);
  readonly searchTerm = signal('');
  readonly selectedState = signal('All');
  readonly selectedDistrict = signal('All');
  readonly selectedVoltageLevel = signal('All');
  readonly selectedStatus = signal('All');
  readonly selectedQca = signal('All');
  readonly minCapacity = signal('');
  readonly maxCapacity = signal('');
  readonly currentPage = signal(1);
  readonly pageSize = signal(8);
  readonly isLinkedCompaniesModalOpen = signal(false);
  readonly selectedPssCode = signal<string | null>(null);
  readonly activeCompanyTab = signal<'available' | 'linked'>('available');
  readonly availableCompanySearch = signal('');
  readonly linkedCompanySearch = signal('');
  readonly selectedAvailableCompanyNos = signal<string[]>([]);
  readonly selectedLinkedCompanyNos = signal<string[]>([]);

  readonly title = computed(() => this.route.snapshot.data['title'] as string || this.pageData().header.title);
  readonly description = computed(() => this.route.snapshot.data['description'] as string || this.pageData().header.description);
  readonly stats = computed<DashboardStatWithValue[]>(() => {
    const rows = this.rows();
    const totalCapacity = rows.reduce((total, row) => total + row.installedCapacityMw, 0);
    const values: Record<string, string> = {
      'Total PSS': String(rows.length),
      'Active PSS': String(rows.filter((row) => row.status === 'Active').length),
      'Inactive PSS': String(rows.filter((row) => row.status === 'Inactive').length),
      'Total Capacity': totalCapacity.toLocaleString('en-IN'),
      'PSS with Generators': String(rows.filter((row) => row.generatorCount > 0).length),
      'PSS with Meters': String(rows.filter((row) => row.meterCount > 0).length)
    };

    return this.pageData().stats.map((card) => ({
      ...card,
      value: values[card.label] ?? '0'
    }));
  });
  readonly filters = computed(() => this.pageData().filters);
  readonly rows = computed(() => this.pageData().rows);
  readonly selectedPss = computed(() => this.rows().find((row) => row.code === this.selectedPssCode()) || null);
  readonly generatingCompanyCatalog = computed(() => this.pageData().generatingCompanyCatalog);
  readonly linkedCompanies = computed(() => {
    const pssCode = this.selectedPssCode();
    if (!pssCode) {
      return [];
    }

    const linkedRegistrationNos = new Set(this.pageData().pssGeneratingCompanyMappings[pssCode] || []);
    return this.generatingCompanyCatalog().filter((item) => linkedRegistrationNos.has(item.registrationNo));
  });
  readonly availableCompanies = computed(() => {
    const pssCode = this.selectedPssCode();
    if (!pssCode) {
      return [];
    }
    const linkedRegistrationNos = new Set(this.pageData().pssGeneratingCompanyMappings[pssCode] || []);
    return this.generatingCompanyCatalog().filter((item) => !linkedRegistrationNos.has(item.registrationNo));
  });
  readonly filteredAvailableCompanies = computed(() => {
    const search = this.availableCompanySearch().trim().toLowerCase();
    return this.availableCompanies().filter((item) =>
      !search || [item.registrationNo, item.entityName, item.username, item.email].some((value) => value.toLowerCase().includes(search))
    );
  });
  readonly filteredLinkedCompanies = computed(() => {
    const search = this.linkedCompanySearch().trim().toLowerCase();
    return this.linkedCompanies().filter((item) =>
      !search || [item.registrationNo, item.entityName, item.username, item.email].some((value) => value.toLowerCase().includes(search))
    );
  });

  readonly filteredRows = computed(() => {
    const search = this.searchTerm().trim().toLowerCase();
    const selectedState = this.selectedState();
    const selectedDistrict = this.selectedDistrict();
    const selectedVoltageLevel = this.selectedVoltageLevel();
    const selectedStatus = this.selectedStatus();
    const selectedQca = this.selectedQca();
    const minCapacity = Number(this.minCapacity());
    const maxCapacity = Number(this.maxCapacity());
    const hasMin = this.minCapacity().trim() !== '' && !Number.isNaN(minCapacity);
    const hasMax = this.maxCapacity().trim() !== '' && !Number.isNaN(maxCapacity);

    return this.rows().filter((row) => {
      const qcaLabel = `${row.qcaName} (${row.qcaCode})`;
      const matchesSearch = !search || [row.code, row.name, row.qcaName].some((value) => value.toLowerCase().includes(search));
      const matchesState = selectedState === 'All' || row.state === selectedState;
      const matchesDistrict = selectedDistrict === 'All' || row.district === selectedDistrict;
      const matchesVoltage = selectedVoltageLevel === 'All' || String(row.voltageLevelKv) === selectedVoltageLevel;
      const matchesStatus = selectedStatus === 'All' || row.status === selectedStatus;
      const matchesQca = selectedQca === 'All' || qcaLabel === selectedQca;
      const matchesMin = !hasMin || row.installedCapacityMw >= minCapacity;
      const matchesMax = !hasMax || row.installedCapacityMw <= maxCapacity;

      return matchesSearch && matchesState && matchesDistrict && matchesVoltage && matchesStatus && matchesQca && matchesMin && matchesMax;
    });
  });

  readonly totalPages = computed(() => Math.max(1, Math.ceil(this.filteredRows().length / this.pageSize())));
  readonly paginatedRows = computed(() => {
    const page = Math.min(this.currentPage(), this.totalPages());
    const start = (page - 1) * this.pageSize();
    return this.filteredRows().slice(start, start + this.pageSize());
  });
  readonly pageNumbers = computed(() => Array.from({ length: this.totalPages() }, (_, index) => index + 1));
  readonly showingText = computed(() => {
    const total = this.filteredRows().length;
    if (total === 0) {
      return 'Showing 0 entries';
    }
    const start = ((this.currentPage() - 1) * this.pageSize()) + 1;
    const end = Math.min(this.currentPage() * this.pageSize(), total);
    return `Showing ${start} to ${end} of ${total} entries`;
  });

  constructor() {
    this.http.get<PssManagementMock>('json/pss-management.mock.json').subscribe({
      next: (data) => this.pageData.set(data),
      error: () => this.pageData.set(DEFAULT_DATA)
    });
  }

  updateSearch(value: string): void {
    this.searchTerm.set(value);
    this.currentPage.set(1);
  }

  updateState(value: string): void {
    this.selectedState.set(value);
    this.currentPage.set(1);
  }

  updateDistrict(value: string): void {
    this.selectedDistrict.set(value);
    this.currentPage.set(1);
  }

  updateVoltageLevel(value: string): void {
    this.selectedVoltageLevel.set(value);
    this.currentPage.set(1);
  }

  updateStatus(value: string): void {
    this.selectedStatus.set(value);
    this.currentPage.set(1);
  }

  updateQca(value: string): void {
    this.selectedQca.set(value);
    this.currentPage.set(1);
  }

  updateMinCapacity(value: string): void {
    this.minCapacity.set(value);
    this.currentPage.set(1);
  }

  updateMaxCapacity(value: string): void {
    this.maxCapacity.set(value);
    this.currentPage.set(1);
  }

  resetFilters(): void {
    this.searchTerm.set('');
    this.selectedState.set('All');
    this.selectedDistrict.set('All');
    this.selectedVoltageLevel.set('All');
    this.selectedStatus.set('All');
    this.selectedQca.set('All');
    this.minCapacity.set('');
    this.maxCapacity.set('');
    this.currentPage.set(1);
  }

  goToPage(page: number): void {
    this.currentPage.set(Math.min(Math.max(page, 1), this.totalPages()));
  }

  openManageGeneratingCompaniesModal(pssCode: string): void {
    this.selectedPssCode.set(pssCode);
    this.activeCompanyTab.set('available');
    this.availableCompanySearch.set('');
    this.linkedCompanySearch.set('');
    this.selectedAvailableCompanyNos.set([]);
    this.selectedLinkedCompanyNos.set([]);
    this.isLinkedCompaniesModalOpen.set(true);
  }

  closeManageGeneratingCompaniesModal(): void {
    this.isLinkedCompaniesModalOpen.set(false);
    this.selectedAvailableCompanyNos.set([]);
    this.selectedLinkedCompanyNos.set([]);
  }

  switchCompanyTab(tab: 'available' | 'linked'): void {
    this.activeCompanyTab.set(tab);
    this.selectedAvailableCompanyNos.set([]);
    this.selectedLinkedCompanyNos.set([]);
  }

  updateAvailableCompanySearch(value: string): void {
    this.availableCompanySearch.set(value);
  }

  updateLinkedCompanySearch(value: string): void {
    this.linkedCompanySearch.set(value);
  }

  toggleAvailableCompanySelection(registrationNo: string, checked: boolean): void {
    const current = this.selectedAvailableCompanyNos();
    this.selectedAvailableCompanyNos.set(
      checked ? [...current, registrationNo] : current.filter((code) => code !== registrationNo)
    );
  }

  toggleLinkedCompanySelection(registrationNo: string, checked: boolean): void {
    const current = this.selectedLinkedCompanyNos();
    this.selectedLinkedCompanyNos.set(
      checked ? [...current, registrationNo] : current.filter((code) => code !== registrationNo)
    );
  }

  addSelectedCompanies(): void {
    const pssCode = this.selectedPssCode();
    if (!pssCode || this.selectedAvailableCompanyNos().length === 0) {
      return;
    }

    const selected = new Set(this.selectedAvailableCompanyNos());
    this.pageData.update((data) => {
      const currentMappings = data.pssGeneratingCompanyMappings[pssCode] || [];
      const nextMappings = Array.from(new Set([...currentMappings, ...selected]));
      return {
        ...data,
        pssGeneratingCompanyMappings: {
          ...data.pssGeneratingCompanyMappings,
          [pssCode]: nextMappings
        }
      };
    });
    this.selectedAvailableCompanyNos.set([]);
    this.activeCompanyTab.set('linked');
  }

  removeSelectedCompanies(): void {
    const pssCode = this.selectedPssCode();
    if (!pssCode || this.selectedLinkedCompanyNos().length === 0) {
      return;
    }

    const selected = new Set(this.selectedLinkedCompanyNos());
    this.pageData.update((data) => ({
      ...data,
      pssGeneratingCompanyMappings: {
        ...data.pssGeneratingCompanyMappings,
        [pssCode]: (data.pssGeneratingCompanyMappings[pssCode] || []).filter((registrationNo) => !selected.has(registrationNo))
      }
    }));
    this.selectedLinkedCompanyNos.set([]);
  }

  removeLinkedCompany(registrationNo: string): void {
    const pssCode = this.selectedPssCode();
    if (!pssCode) {
      return;
    }

    this.pageData.update((data) => ({
      ...data,
      pssGeneratingCompanyMappings: {
        ...data.pssGeneratingCompanyMappings,
        [pssCode]: (data.pssGeneratingCompanyMappings[pssCode] || []).filter((item) => item !== registrationNo)
      }
    }));
    this.selectedLinkedCompanyNos.set(this.selectedLinkedCompanyNos().filter((item) => item !== registrationNo));
  }

  formatCapacity(value: number): string {
    return new Intl.NumberFormat('en-IN').format(value);
  }
}
